import { useState, useEffect, useCallback, useRef } from 'react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications(token) {
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [supported, setSupported] = useState(false);
  const registrationRef = useRef(null);

  // Check support on mount
  useEffect(() => {
    const hasPush = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setSupported(hasPush);
    if (hasPush) {
      setPermission(Notification.permission);
    }
  }, []);

  // Register service worker and check existing subscription
  useEffect(() => {
    if (!supported || !token) return;

    async function registerSW() {
      try {
        const reg = await navigator.serviceWorker.register('/sw-push.js');
        registrationRef.current = reg;

        const existingSub = await reg.pushManager.getSubscription();
        if (existingSub) {
          setIsSubscribed(true);
          // Re-sync subscription with backend
          await sendSubscriptionToBackend(existingSub, token);
        }
      } catch (err) {
        console.warn('Service worker registration failed:', err);
      }
    }

    registerSW();
  }, [supported, token]);

  const sendSubscriptionToBackend = async (subscription, authToken) => {
    try {
      await fetch(`${API}/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ subscription: subscription.toJSON() }),
      });
    } catch (err) {
      console.warn('Failed to sync push subscription:', err);
    }
  };

  const subscribe = useCallback(async () => {
    if (!supported || !token) return false;

    try {
      // Request permission (must be in user gesture handler)
      const perm = await Notification.requestPermission();
      setPermission(perm);

      if (perm !== 'granted') return false;

      // Get or register service worker
      let reg = registrationRef.current;
      if (!reg) {
        reg = await navigator.serviceWorker.register('/sw-push.js');
        registrationRef.current = reg;
        await navigator.serviceWorker.ready;
      }

      // Subscribe to push
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      // Send to backend
      await sendSubscriptionToBackend(subscription, token);
      setIsSubscribed(true);
      return true;
    } catch (err) {
      console.error('Push subscription failed:', err);
      return false;
    }
  }, [supported, token]);

  const unsubscribe = useCallback(async () => {
    if (!supported || !token) return;

    try {
      const reg = registrationRef.current || await navigator.serviceWorker.getRegistration('/sw-push.js');
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          const endpoint = sub.endpoint;
          await sub.unsubscribe();

          await fetch(`${API}/push/unsubscribe`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ endpoint }),
          });
        }
      }
      setIsSubscribed(false);
    } catch (err) {
      console.warn('Push unsubscribe failed:', err);
    }
  }, [supported, token]);

  return { permission, isSubscribed, supported, subscribe, unsubscribe };
}
